/**
 * CatalogBulkActions — toplu işlem çubuğu ve onay diyalogları.
 *
 * Inventory'deki toplu işlemlerin alt kümesi: sil · aktif/pasif yap · sektör değiştir ·
 * kategori değiştir · KDV güncelle. Fiyat güncelleme yok (katalogda fiyat yok); depo,
 * e-ticaret, QR menü gibi tenant kavramlarının da katalogda karşılığı yok.
 *
 * "Sektör/kategori değiştir" bu fazın en kritik parçası: seed'den gelen sınıflandırılmamış
 * ürünleri toplu seçip doğru sektöre taşımayı sağlıyor.
 *
 * Her işlem, kaç kayda uygulanacağını önceden gösteren bir onay diyaloğuyla çalışır.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import PercentIcon from '@mui/icons-material/Percent';

import CatalogService from '../../../../services/CatalogService';
import { CATALOG_VAT_RATE_OPTIONS, CATALOG_VAT_STATUS_OPTIONS } from '../types/catalog.types';
import type { CatalogCategory, CatalogSector } from '../types/catalog.types';

/** Backend tek toplu işlemde en fazla 2000 kayıt kabul ediyor (MaxBulkSize). */
export const CATALOG_MAX_BULK_SIZE = 2000;

type BulkAction = 'delete' | 'activate' | 'deactivate' | 'move' | 'vat' | null;

export interface CatalogBulkActionsProps {
  selectedIds: number[];
  sectors: CatalogSector[];
  /** Aktif filtreye uyan toplam kayıt — "filtredekilerin tümünü seç" için. */
  totalCount: number;
  /** Filtreye uyan tüm Id'leri çeker; sayfa sahibi uygular (filtreleri o biliyor). */
  onSelectAllFiltered: () => void;
  selectingAll: boolean;
  onClearSelection: () => void;
  /** İşlem bitti: listeyi ve özeti tazele, mesajı göster. */
  onCompleted: (message: string) => void;
}

const CatalogBulkActions: React.FC<CatalogBulkActionsProps> = ({
  selectedIds,
  sectors,
  totalCount,
  onSelectAllFiltered,
  selectingAll,
  onClearSelection,
  onCompleted,
}) => {
  const [action, setAction] = useState<BulkAction>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Taşıma hedefi
  const [targetSector, setTargetSector] = useState<CatalogSector | null>(null);
  const [targetCategory, setTargetCategory] = useState<CatalogCategory | null>(null);
  const [targetCategories, setTargetCategories] = useState<CatalogCategory[]>([]);

  // KDV hedefi
  const [targetVatRate, setTargetVatRate] = useState<number | null>(null);
  const [targetVatStatus, setTargetVatStatus] = useState<string | null>(null);

  const count = selectedIds.length;
  const hasSelection = count > 0;

  // Taşıma diyaloğunda kategori listesi sektöre bağlı kademeli gelir.
  useEffect(() => {
    if (action !== 'move' || !targetSector) {
      setTargetCategories([]);
      return;
    }

    let cancelled = false;
    CatalogService.getCategories(targetSector.id)
      .then((rows) => {
        if (!cancelled) setTargetCategories(rows);
      })
      .catch((err) => {
        console.error('Hedef kategoriler yüklenemedi:', err);
        if (!cancelled) setTargetCategories([]);
      });

    return () => {
      cancelled = true;
    };
  }, [action, targetSector]);

  const orderedTargetCategories = useMemo(() => {
    const roots = targetCategories.filter((c) => !c.parentId);
    const childrenOf = new Map<number, CatalogCategory[]>();
    targetCategories.forEach((c) => {
      if (!c.parentId) return;
      if (!childrenOf.has(c.parentId)) childrenOf.set(c.parentId, []);
      childrenOf.get(c.parentId)!.push(c);
    });

    const ordered: CatalogCategory[] = [];
    roots.forEach((root) => {
      ordered.push(root);
      (childrenOf.get(root.id) ?? []).forEach((child) => ordered.push(child));
    });
    return ordered;
  }, [targetCategories]);

  const closeDialog = () => {
    setAction(null);
    setError(null);
    setTargetSector(null);
    setTargetCategory(null);
    setTargetVatRate(null);
    setTargetVatStatus(null);
  };

  const run = async (fn: () => Promise<{ message: string }>) => {
    setRunning(true);
    setError(null);
    try {
      const result = await fn();
      onCompleted(result.message);
      closeDialog();
      onClearSelection();
    } catch (err: any) {
      setError(err?.message || 'İşlem başarısız.');
    } finally {
      setRunning(false);
    }
  };

  const tooManyToSelectAll = totalCount > CATALOG_MAX_BULK_SIZE;

  return (
    <>
      <Paper
        elevation={2}
        sx={{
          p: 1.5,
          mb: 2,
          borderRadius: 2,
          borderLeft: '4px solid',
          borderColor: 'warning.main',
          bgcolor: 'rgba(255, 152, 0, 0.06)',
        }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
          <Chip
            color={hasSelection ? 'primary' : 'default'}
            label={`${count.toLocaleString('tr-TR')} kayıt seçili`}
            sx={{ fontWeight: 600 }}
          />

          <Tooltip
            title={
              tooManyToSelectAll
                ? `Filtreye ${totalCount.toLocaleString('tr-TR')} kayıt uyuyor; tek seferde en fazla ${CATALOG_MAX_BULK_SIZE.toLocaleString('tr-TR')} kayıt işlenebilir. Önce filtreyi daraltın.`
                : 'Sadece bu sayfayı değil, filtreye uyan tüm kayıtları seçer'
            }
            arrow
          >
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={<DoneAllIcon />}
                onClick={onSelectAllFiltered}
                disabled={selectingAll || tooManyToSelectAll || totalCount === 0}
              >
                {selectingAll
                  ? 'Seçiliyor...'
                  : `Filtredeki tümünü seç (${totalCount.toLocaleString('tr-TR')})`}
              </Button>
            </span>
          </Tooltip>

          {hasSelection && (
            <Button size="small" color="inherit" onClick={onClearSelection}>
              Seçimi temizle
            </Button>
          )}

          <Box sx={{ flexGrow: 1 }} />

          <Button
            size="small"
            variant="contained"
            startIcon={<DriveFileMoveIcon />}
            onClick={() => setAction('move')}
            disabled={!hasSelection}
          >
            Sektör / Kategori Değiştir
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<PercentIcon />}
            onClick={() => setAction('vat')}
            disabled={!hasSelection}
          >
            KDV Güncelle
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="success"
            startIcon={<CheckCircleOutlineIcon />}
            onClick={() => setAction('activate')}
            disabled={!hasSelection}
          >
            Aktif Yap
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="warning"
            startIcon={<HighlightOffIcon />}
            onClick={() => setAction('deactivate')}
            disabled={!hasSelection}
          >
            Pasif Yap
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="error"
            startIcon={<DeleteOutlineIcon />}
            onClick={() => setAction('delete')}
            disabled={!hasSelection}
          >
            Sil
          </Button>
        </Box>
      </Paper>

      {/* ─── Sil ─── */}
      <Dialog open={action === 'delete'} onClose={closeDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Toplu silmeyi onayla</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <DialogContentText>
            <strong>{count.toLocaleString('tr-TR')} ürün</strong> katalogdan silinecek. Kayıtlar
            soft-delete ile işaretlenir, listede görünmez.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Vazgeç</Button>
          <Button
            color="error"
            variant="contained"
            disabled={running}
            onClick={() => run(() => CatalogService.bulkDeleteProducts(selectedIds))}
          >
            {running ? 'Siliniyor...' : 'Sil'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Aktif / Pasif ─── */}
      <Dialog
        open={action === 'activate' || action === 'deactivate'}
        onClose={closeDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{action === 'activate' ? 'Aktif yap' : 'Pasif yap'}</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <DialogContentText>
            <strong>{count.toLocaleString('tr-TR')} ürün</strong>{' '}
            {action === 'activate' ? 'aktif' : 'pasif'} yapılacak.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Vazgeç</Button>
          <Button
            variant="contained"
            disabled={running}
            onClick={() =>
              run(() => CatalogService.bulkUpdateStatus(selectedIds, action === 'activate'))
            }
          >
            {running ? 'Uygulanıyor...' : 'Uygula'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Sektör / kategori taşıma ─── */}
      <Dialog open={action === 'move'} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Sektör / kategori değiştir</DialogTitle>
        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {count.toLocaleString('tr-TR')} ürün seçili hedefe taşınacak.
          </Typography>

          <Autocomplete
            fullWidth
            size="small"
            sx={{ mb: 2 }}
            options={sectors}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            value={targetSector}
            onChange={(_, newValue) => {
              setTargetSector(newValue);
              setTargetCategory(null);
            }}
            slotProps={{ popper: { disablePortal: true } }}
            renderInput={(params) => <TextField {...params} required label="Hedef Sektör" />}
          />

          <Autocomplete
            fullWidth
            size="small"
            disabled={!targetSector}
            options={orderedTargetCategories}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            value={targetCategory}
            onChange={(_, newValue) => setTargetCategory(newValue)}
            slotProps={{ popper: { disablePortal: true } }}
            noOptionsText="Bu sektörde kategori yok"
            renderOption={(props, option) => {
              const { key, ...optionProps } = props as any;
              return (
                <li key={option.id} {...optionProps}>
                  <Typography
                    variant="body2"
                    sx={{ pl: option.parentId ? 2 : 0, fontWeight: option.parentId ? 400 : 600 }}
                  >
                    {option.name}
                  </Typography>
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Hedef Kategori"
                helperText="Boş bırakılırsa ürünler kategorisiz kalır"
              />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Vazgeç</Button>
          <Button
            variant="contained"
            disabled={running || !targetSector}
            onClick={() =>
              run(() =>
                CatalogService.bulkMoveProducts(
                  selectedIds,
                  targetSector!.id,
                  targetCategory?.id ?? null
                )
              )
            }
          >
            {running ? 'Taşınıyor...' : 'Taşı'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── KDV güncelleme ─── */}
      <Dialog open={action === 'vat'} onClose={closeDialog} maxWidth="xs" fullWidth>
        <DialogTitle>KDV bilgisini güncelle</DialogTitle>
        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {count.toLocaleString('tr-TR')} ürün güncellenecek. Boş bıraktığınız alan
            değiştirilmez.
          </Typography>

          <Autocomplete
            fullWidth
            size="small"
            sx={{ mb: 2 }}
            options={CATALOG_VAT_RATE_OPTIONS}
            getOptionLabel={(option) => `%${option}`}
            value={targetVatRate}
            onChange={(_, newValue) => setTargetVatRate(newValue)}
            slotProps={{ popper: { disablePortal: true } }}
            renderInput={(params) => <TextField {...params} label="KDV Oranı" />}
          />

          <Autocomplete
            fullWidth
            size="small"
            options={CATALOG_VAT_STATUS_OPTIONS}
            value={targetVatStatus}
            onChange={(_, newValue) => setTargetVatStatus(newValue)}
            slotProps={{ popper: { disablePortal: true } }}
            renderInput={(params) => <TextField {...params} label="KDV Durumu" />}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Vazgeç</Button>
          <Button
            variant="contained"
            disabled={running || (targetVatRate === null && !targetVatStatus)}
            onClick={() =>
              run(() => CatalogService.bulkUpdateVat(selectedIds, targetVatRate, targetVatStatus))
            }
          >
            {running ? 'Uygulanıyor...' : 'Uygula'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CatalogBulkActions;
